import { useEffect, useState } from 'react';
import { ArrowSquareOut } from 'shared/icons';
import api from '../../services/api';
import { FormatMoney } from '../../utils/format';
import { MLB_URLS } from '../../utils/ml';
import { DASH, ML_STATUS_BADGES, Pill } from './campaignCardShared';

/**
 * FAMÍLIA DO ANÚNCIO — o anúncio de lista geral e sua cópia de catálogo,
 * LADO A LADO.
 *
 * No painel do Mercado Livre os dois aparecem como um só ("sincronizado com
 * #MLB…"); na API são dois itens, cada um com suas campanhas, seu preço
 * vigente e seu rebate. O monitor lista um card por item, então sem esta
 * tabela o dono comparava a campanha da cópia com o preço do original sem
 * saber que eram anúncios diferentes.
 *
 * Só leitura. Busca ao abrir (é um par, não uma lista) — o dado vem do nosso
 * banco, não do ML, então reabrir é barato.
 */

const GRID_COLS = 'grid grid-cols-[minmax(0,2fr)_0.8fr_0.8fr_minmax(0,1.6fr)_minmax(0,1.4fr)] gap-2';

const LINK_LABELS = {
    seed: null,
    item_relations: 'vínculo do ML',
    user_product: 'mesmo produto',
};

function Money(value) {
    return Number.isFinite(Number(value)) && value !== null ? FormatMoney(Number(value)) : DASH;
}

const CampaignItemFamily = ({ itemId }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        api.getItemFamily(itemId)
            .then((res) => {
                if (!cancelled) setData(res);
            })
            .catch((err) => {
                if (!cancelled) setError(err?.message || 'Não foi possível carregar a família do anúncio.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [itemId]);

    if (loading) {
        return <p className="px-1 text-[11px] text-mono-500 dark:text-mono-400">Carregando família…</p>;
    }
    if (error) {
        return <p className="px-1 text-[11px] text-danger-600 dark:text-danger-400">{error}</p>;
    }
    const members = data?.members || [];
    if (members.length <= 1) {
        return (
            <p className="px-1 text-[11px] italic text-mono-500 dark:text-mono-400">
                O Mercado Livre não informou outro anúncio vinculado a este.
            </p>
        );
    }

    return (
        <div className="space-y-1.5">
            <div className={`${GRID_COLS} px-1 text-[10px] font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400`}>
                <span>Anúncio</span>
                <span className="text-right">Preço lista</span>
                <span className="text-right">Vigente</span>
                <span>Campanha ativa</span>
                <span>Melhor opção</span>
            </div>
            {members.map((member) => {
                const status = ML_STATUS_BADGES[member.ml_status] || { label: member.ml_status || DASH, tone: 'mono' };
                const current = member.current_campaign;
                const best = member.best_campaign;
                const linkLabel = LINK_LABELS[member.link];
                return (
                    <div
                        key={member.item_id}
                        className={`${GRID_COLS} items-start rounded-md px-1 py-1 text-[11px] ${
                            member.is_seed ? 'bg-white dark:bg-mono-950' : 'bg-transparent'
                        }`}
                    >
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-1">
                                <a
                                    href={MLB_URLS.search(member.item_id)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="font-mono text-danger-600 hover:underline dark:text-danger-400"
                                >
                                    {member.item_id}
                                </a>
                                <Pill tone={member.catalog_listing ? 'info' : 'mono'} className="uppercase">
                                    {member.catalog_listing ? 'catálogo' : 'lista'}
                                </Pill>
                                <Pill tone={status.tone} className="uppercase">
                                    {status.label}
                                </Pill>
                                {member.is_seed ? (
                                    <span className="text-[10px] text-mono-500 dark:text-mono-400">este card</span>
                                ) : linkLabel ? (
                                    <span className="text-[10px] text-mono-500 dark:text-mono-400">{linkLabel}</span>
                                ) : null}
                            </div>
                            <p className="truncate text-mono-700 dark:text-mono-300" title={member.title || ''}>
                                {member.title || DASH}
                            </p>
                        </div>
                        <span className="text-right font-mono tabular-nums text-black dark:text-white">
                            {Money(member.price)}
                        </span>
                        <span className="text-right font-mono tabular-nums text-black dark:text-white">
                            {Money(member.sale_price_amount)}
                        </span>
                        <div className="min-w-0">
                            {current ? (
                                <>
                                    <p className="truncate text-mono-700 dark:text-mono-300" title={current.campaign_name || ''}>
                                        {current.campaign_name || current.campaign_id}
                                    </p>
                                    <p className="font-mono text-[10px] text-mono-500 dark:text-mono-400">
                                        {current.campaign_type} · rebate {current.rebate_percent ?? DASH}%
                                        {current.source === 'sale_price' ? '' : ' · deduzida'}
                                    </p>
                                </>
                            ) : (
                                <span className="italic text-mono-400">sem campanha ativa</span>
                            )}
                        </div>
                        <div className="min-w-0">
                            {best ? (
                                <>
                                    <p className="truncate text-mono-700 dark:text-mono-300" title={best.campaign_name || ''}>
                                        {best.campaign_name || best.campaign_id}
                                    </p>
                                    <p className="font-mono text-[10px] text-mono-500 dark:text-mono-400">
                                        {member.campaigns_available_count} elegíve{member.campaigns_available_count === 1 ? 'l' : 'is'}
                                    </p>
                                </>
                            ) : member.is_on_best_campaign ? (
                                <Pill tone="success">JÁ ESTÁ NA MELHOR</Pill>
                            ) : (
                                <span className="text-mono-400">
                                    {member.campaigns_available_count > 0
                                        ? `${member.campaigns_available_count} elegíveis`
                                        : DASH}
                                </span>
                            )}
                        </div>
                    </div>
                );
            })}
            <p className="flex items-center gap-1 px-1 text-[10px] text-mono-500 dark:text-mono-400">
                Vínculo informado pelo Mercado Livre. Preço e estoque são sincronizados entre os dois; campanhas e
                rebate são por anúncio.
                <a
                    href={MLB_URLS.promos(itemId)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-0.5 text-info-600 hover:underline dark:text-info-400"
                >
                    abrir no ML <ArrowSquareOut size={10} />
                </a>
            </p>
        </div>
    );
};

export default CampaignItemFamily;
